import { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";

import "../../styles/GroupShare.scss";
import "../../styles/SimpleAutoSuggest.scss";

/**
 * A simple auto-suggest input component used to search and select from a list of options.
 * Typically used to select employees or users by name.
 *
 * @component
 * @param {Object} props
 * @param {{ who: string }} formState - Object containing form state values.
 * @param {Function} handleChange - Callback fired when the input value changes.
 * @param {Function} handleOptionClick - Callback fired when a suggestion option is clicked.
 * @param {string} name - Context name, used to determine display logic (e.g., "groupShare").
 * @param {string} [noneFoundMessage] - Message to display when no results are found.
 * @param {Object[]} [options] - Array of DTOs (Data Transfer Objects) to show in suggestions.
 * @param {string} [searchLabel] - Label displayed above the input field.
 * @param {Function} setShow - Function to set whether the dropdown should be shown.
 * @param {boolean} [show] - Boolean flag indicating whether the dropdown is visible.
 * @returns {JSX.Element}
 */
const SimpleAutoSuggest = ({
    formState,
    handleChange,
    handleOptionClick,
    name,
    noneFoundMessage,
    options,
    searchLabel,
    setShow,
    show
}) => {
    const inputRef = useRef(null);
    const [dropdownStyle, setDropdownStyle] = useState({});

    const handleSelect = (dto) => {
        setShow(false);
        handleOptionClick(dto);
    };

    /**
     * Effect to dynamically calculate and set the dropdown position and size
     * based on the input field's position and width.
     */
    useEffect(() => {
        if (show && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            const offsetTop = rect.height + 24; // or however much space you want below input
            setDropdownStyle({
                position: "absolute",
                top: `${offsetTop}px`,
                left: "0px",
                width: `${rect.width}px`,
                zIndex: 999
            });
        }
    }, [show]);

    return (
        <section className="sas-container">
            <label htmlFor="who-employee">
                <div>{searchLabel}</div>
                <input
                    className="sas-input"
                    id="who-employee"
                    name="who"
                    type="text"
                    onChange={handleChange}
                    value={formState.who}
                    ref={inputRef}
                />
            </label>

            {options?.length > 0 && show && (
                <div className="sas-auto-suggest" style={dropdownStyle}>
                    <div className="el-icon-container">
                        <i className="bi bi-chevron-up" onClick={() => setShow(false)} role="button" tabIndex={0} />
                    </div>
                    {options.map((dto, index) => {
                        const key = `autoSuggest-${index}`;
                        if (name === "groupShare") {
                            return (
                                <button
                                    key={key}
                                    className="employee-lookup-btn"
                                    type="button"
                                    onClick={() => handleSelect(dto)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") handleSelect(dto);
                                    }}
                                >
                                    <div className="employee-btn-layout">
                                        <div>{dto.fullName}</div>
                                        {dto.school && (
                                            <div className="el-school">
                                                {dto.school} - {dto.emailAddress}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        }
                        return null;
                    })}
                </div>
            )}

            {!options?.length && formState.who.length >= 3 && show && (
                <div className="sas-none-found" style={dropdownStyle}>
                    <div className="el-icon-container">
                        <i className="bi bi-chevron-up" onClick={() => setShow(false)} role="button" tabIndex={0} />
                    </div>
                    <div className="none-found-message">{noneFoundMessage}</div>
                </div>
            )}
        </section>
    );
};

SimpleAutoSuggest.propTypes = {
    formState: PropTypes.shape({
        who: PropTypes.string.isRequired
    }).isRequired,
    handleChange: PropTypes.func.isRequired,
    handleOptionClick: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    noneFoundMessage: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.object),
    searchInstructions: PropTypes.string,
    searchLabel: PropTypes.string,
    setShow: PropTypes.func.isRequired,
    show: PropTypes.bool
};

SimpleAutoSuggest.defaultProps = {
    noneFoundMessage: "",
    options: [],
    searchLabel: "",
    show: false
};

export default SimpleAutoSuggest;
